import React from 'react';
import {
  AlertTriangle,
  Archive,
  Bell,
  ChevronDown,
  ClipboardList,
  FileUp,
  Package,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import { dashboardService, AccountableDashboardSummary } from '../../../services/dashboardService';
import { authService } from '../../../services/authService';
import { FirearmIncidentPayload, inventoryService, OfficerPayload } from '../../../services/inventoryService';
import { issuanceService } from '../../../services/issuanceService';
import { notificationService } from '../../../services/notificationService';
import { PaginatedResult } from '../../../services/apiClient';
import { permissionService } from '../../../services/permissionService';
import repairService from '../../../services/repairService';
import { ARE, DepartmentSummary, Firearm, FirearmRepair, Issuance, MultiIssuanceAttempt, Notification, ReturnRecord, User } from '../../../types/domain';
import {
  FIREARM_EVIDENCE_ALLOWED_MIME_TYPES,
  FIREARM_EVIDENCE_MAX_BYTES,
} from '../../../types/firearmEvidence';
import { useAdminDepartmentContext } from '../../../hooks/useAdminDepartmentContext';
import { useReferenceOptions } from '../../../hooks/useReferenceData';
import { withAdminDepartmentParam } from '../../../utils/adminViewMode';
import { todayISO } from '../../../utils/dateValidation';
import { markNotificationReadIfUnread } from '../../../utils/notificationEvents';
import { canonicalStatusKey, canonicalStatusLabel } from '../../../utils/statusPresentation';
import DismissibleError from '../../DismissibleError';
import CollapsibleSection from '../CollapsibleSection';
import {
  CompactTableToolbar,
  DetailDrawer,
  DocumentPreviewModal,
  EmptyTableState,
  FirearmIssuanceDetailsDrawer,
  FirearmMasterImagePanel,
  FormModal,
  NotificationInbox,
  NotificationInboxItem,
  RecordsTableCard,
  StatusBadge,
  TypeBadge,
  WorkflowFeedback,
} from '../../records';

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const today = todayISO;
const statusText = (status?: string): string =>
  value(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const assignedText = (firearm: Firearm): string => {
  if (firearm.assigned_to_name || firearm.assigned_to_badge) {
    return value(firearm.assigned_to_name || firearm.assigned_to_badge);
  }
  if (firearm.status === 'in_storage') return canonicalStatusLabel(firearm);
  return 'Unassigned';
};

const officerName = (officer: any): string =>
  `${officer.first_name || ''} ${officer.middle_name || ''} ${officer.last_name || ''}`
    .replace(/\s+/g, ' ')
    .trim() || '-';

const normalized = (input: unknown): string => value(input, '').trim().toLowerCase();
const referenceValue = (input: unknown): string =>
  normalized(input).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const formatDate = (date?: string | null): string => {
  if (!date) return '-';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString();
};

type LtopfStatus = 'valid' | 'expiring_soon' | 'expired' | 'missing_expiration';

const ltopfStatus = (officer: any): LtopfStatus => {
  const reportedStatus = normalized(officer.ltopf_status);
  if (reportedStatus === 'missing') return 'missing_expiration';
  if (reportedStatus === 'valid' || reportedStatus === 'expiring_soon' || reportedStatus === 'expired' || reportedStatus === 'missing_expiration') {
    return reportedStatus;
  }
  const expirationDate = officer.ltopf_expiration_date ? String(officer.ltopf_expiration_date).slice(0, 10) : '';
  if (!expirationDate) return 'missing_expiration';
  const todayDate = today();
  if (expirationDate < todayDate) return 'expired';
  const [expirationYear, expirationMonth, expirationDay] = expirationDate.split('-').map(Number);
  const [todayYear, todayMonth, todayDay] = todayDate.split('-').map(Number);
  const expirationTime = Date.UTC(expirationYear, expirationMonth - 1, expirationDay);
  const todayTime = Date.UTC(todayYear, todayMonth - 1, todayDay);
  const daysUntilExpiration = Math.round((expirationTime - todayTime) / (1000 * 60 * 60 * 24));
  return daysUntilExpiration <= 60 ? 'expiring_soon' : 'valid';
};

const ltopfStatusLabel = (status: LtopfStatus): string => {
  if (status === 'valid') return 'Valid';
  if (status === 'expiring_soon') return 'Expiring Soon';
  if (status === 'expired') return 'Expired';
  return 'Missing';
};

const ltopfTone = (status: LtopfStatus) => {
  if (status === 'expired' || status === 'missing_expiration') return 'danger';
  if (status === 'expiring_soon') return 'warning';
  return 'success';
};

type EmploymentStatus = 'active' | 'inactive' | 'suspended' | 'retired';
const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'retired', label: 'Retired' },
];

const employmentStatus = (officer: any): EmploymentStatus => {
  const status = normalized(officer.employment_status);
  if (status === 'active' || status === 'inactive' || status === 'suspended' || status === 'retired') {
    return status;
  }
  return 'inactive';
};

const employmentEditOptions = (current?: EmploymentStatus) => {
  if (!current) return EMPLOYMENT_STATUS_OPTIONS.filter((option) => option.value === 'active');
  const allowed: Record<EmploymentStatus, EmploymentStatus[]> = {
    active: ['active', 'suspended', 'retired'],
    inactive: ['inactive'],
    suspended: ['suspended', 'retired'],
    retired: ['retired'],
  };
  return EMPLOYMENT_STATUS_OPTIONS.filter((option) =>
    allowed[current].includes(option.value as EmploymentStatus)
  );
};

const employmentStatusLabel = (status: EmploymentStatus): string => {
  if (status === 'active') return 'Active';
  if (status === 'inactive') return 'Inactive';
  if (status === 'suspended') return 'Suspended';
  return 'Retired';
};

const employmentTone = (status: EmploymentStatus) => {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'warning';
  return 'neutral';
};

const isReturnEligibleIssuance = (issuance: Issuance): boolean =>
  normalized(issuance.status) === 'issued'
  && !issuance.returned_at
  && normalized(issuance.firearm_status) === 'issued';

const has = (permission: string): boolean => permissionService.hasPermission(permission);

// Invisible implementation note for FRS traceability.
// Intentionally returns null so these notes do not render in the UI.
const FRSNote: React.FC<{ children: React.ReactNode }> = () => null;

const EmptyState: React.FC<{ title: string; copy: string }> = ({ title, copy }) => (
  <div className="workspace-empty">
    <Package size={22} />
    <strong>{title}</strong>
    <p>{copy}</p>
  </div>
);

const formatFieldValue = (input: unknown): string => {
  if (Array.isArray(input)) return input.map(formatFieldValue).join(' ');
  if (input && typeof input === 'object') {
    return Object.entries(input)
      .map(([key, value]) => `${key}: ${formatFieldValue(value)}`)
      .join('; ');
  }
  return value(input, '');
};

const fieldLabel = (field: string): string => {
  const labels: Record<string, string> = {
    are_document: 'ARE document',
    are_expiration_date: 'ARE expiration date',
    firearm: 'Firearm',
    non_field_errors: 'Non-field errors',
    officer: 'Officer',
  };
  return labels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const collectErrorMessages = (input: unknown): string[] => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return [];
  return Object.entries(input).flatMap(([key, fieldValue]) => {
    if (key === 'error' && /invalid data provided/i.test(formatFieldValue(fieldValue))) return [];
    if ((key === 'details' || key === 'errors') && fieldValue && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      return collectErrorMessages(fieldValue);
    }
    if (key === 'detail' && (!fieldValue || typeof fieldValue !== 'object')) {
      return [formatFieldValue(fieldValue)].filter(Boolean);
    }
    const message = formatFieldValue(fieldValue);
    return message ? [`${fieldLabel(key)}: ${message}`] : [];
  });
};

const errorText = (err: any, fallback: string): string => {
  const data = err?.response?.data || err?.originalError?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const fieldMessages = collectErrorMessages(data);
    if (fieldMessages.length > 0) return fieldMessages.join('; ');
    if (data.detail) return formatFieldValue(data.detail);
    if (data.error && !/invalid data provided/i.test(String(data.error))) return formatFieldValue(data.error);
  }
  return err?.message || fallback;
};

const isSupportedIssuanceArticle = (article?: string): boolean => {
  const value = (article || '').trim().toLowerCase().replace(/_/g, ' ');
  return value === 'pistol' || value === 'revolver';
};

const issuanceDisplayLabel = (issuance: Issuance): string => {
  if (issuance.status_display) return issuance.status_display;
  const fallback: Record<string, string> = {
    issued: 'Issued',
    handover_pending: 'For Acknowledgment',
    accountable_confirmed: 'Active',
    completed: 'Active',
  };
  return fallback[issuance.status] || statusText(issuance.status);
};

const allowedIssuanceStatuses = [
  'in_storage',
];

const closedIssuanceStatuses = new Set(['rejected', 'returned', 'revoked', 'cancelled', 'canceled', 'incident_closed']);

const isClosedIssuance = (issuance: Issuance): boolean =>
  closedIssuanceStatuses.has(normalized(issuance.status));

const shouldShowAreRequirement = (issuance: Issuance): boolean => {
  const metadataMultiIssuance = issuance.metadata?.multi_issuance;
  return !isClosedIssuance(issuance)
    && !metadataMultiIssuance?.approval_blocked
    && !issuance.multi_issuance?.approval_blocked
    && normalized(issuance.status) !== 'multi_issuance';
};

const hasMultiIssuance = (issuance: Issuance): boolean =>
  Boolean(issuance.multi_issuance?.detected);

const multiIssuanceSummary = (issuance: Issuance): string => {
  const multi = issuance.multi_issuance;
  if (!multi?.detected) return '';
  if (issuance.status === 'rejected' && multi.resolved) return 'Closed due to Multi-Issuance';
  const conflictTypes = new Set(multi.conflict_types || []);
  if (conflictTypes.has('same_firearm_multiple_officers')) return 'Same firearm conflict';
  return 'Conflict — Multi-Issuance';
};

const conflictTypeLabel = (type: string): string => {
  if (type === 'same_firearm_multiple_officers') return 'Same firearm conflict';
  return statusText(type);
};

const canonicalConflictText = (text?: string): string =>
  value(text, '').replace(/\bMulti Issuance\b/g, 'Multi-Issuance');

const multiIssuanceStatusText = (issuance: Issuance): string => {
  const multi = issuance.multi_issuance;
  if (!multi?.detected) return '-';
  if (multi.resolved) return 'Resolved';
  if (multi.flagged || issuance.status === 'multi_issuance') return 'Flagged';
  return 'Detected';
};

const multiIssuanceDetailRows = (issuance: Issuance): Array<[string, string]> => {
  const multi = issuance.multi_issuance || {};
  const details = multi.details || {};
  const sameFirearm = details.same_firearm_multiple_officers || {};
  return [
    ['Current issuance status', statusText(issuance.status_display || issuance.status)],
    ['Multi-Issuance status', multiIssuanceStatusText(issuance)],
    ['Conflict type', (multi.conflict_types || []).map(conflictTypeLabel).join(', ')],
    ['Firearm serial', sameFirearm.firearm_serial_number || issuance.firearm_serial],
    ['Current holder', sameFirearm.current_officer_name],
    ['Attempted holder', sameFirearm.attempted_officer_name || issuance.officer_name],
    ['Blocking issuance ID', sameFirearm.blocking_issuance_id],
    ['Resolved by', value(multi.resolved_by, '')],
    ['Resolved at', formatDate(multi.resolved_at)],
    ['Resolution method', statusText(multi.resolution_method)],
    ['Resolution message', canonicalConflictText(multi.resolution_message)],
    ['Original system message', sameFirearm.message],
  ].filter(([, detail]) => Boolean(detail && detail !== '-')) as Array<[string, string]>;
};

const areDocumentName = (are: ARE): string =>
  value(are.document_original_name || (are.scanned_document ? are.scanned_document.split('/').pop() : ''), '');
const hasAreDocument = (are?: ARE | null): boolean =>
  Boolean(are && (are.document_original_name || are.scanned_document || are.document));
const isAreUploaded = (are?: ARE | null): boolean =>
  Boolean(are && are.expiration_date && hasAreDocument(are));
const areUploadStatus = (are?: ARE | null): string => {
  if (!are) return 'pending_accountable_are';
  if (normalized(are.status) === 'rejected') return 'rejected';
  if (isAreUploaded(are)) return 'are_uploaded';
  if (hasAreDocument(are) && !are.expiration_date) return 'missing_expiration';
  return 'pending_accountable_are';
};
const areUploadStatusLabel = (are?: ARE | null): string => {
  const status = areUploadStatus(are);
  if (status === 'are_uploaded') return 'ARE Uploaded';
  if (status === 'missing_expiration') return 'Missing Expiration';
  if (status === 'rejected') return 'Rejected';
  return 'Pending Accountable ARE';
};
const canonicalAreStatus = (are?: ARE | null): string => {
  if (!are) return 'not_uploaded';
  if (are.are_status) return are.are_status;
  if (normalized(are.status) === 'rejected') return 'rejected';
  if (isAreUploaded(are)) return 'uploaded';
  return hasAreDocument(are) ? 'pending_review' : 'not_uploaded';
};
const areUploadTone = (are?: ARE | null): 'success' | 'warning' | 'danger' => {
  const status = areUploadStatus(are);
  if (status === 'are_uploaded') return 'success';
  if (status === 'rejected') return 'danger';
  return 'warning';
};
const areExpirationLabel = (are: ARE): string => {
  if (are.expires_today) return 'Expires Today';
  return statusText(are.expiration_status || 'missing_expiration');
};
const areExpirationTone = (are: ARE): 'success' | 'warning' | 'danger' => {
  if (are.expiration_status === 'expired') return 'danger';
  if (are.expiration_status === 'valid') return 'success';
  return 'warning';
};
const areDocumentPreviewLabel = (are: ARE): string => {
  const filename = areDocumentName(are).toLowerCase();
  const mimeType = (are.document_mime_type || '').toLowerCase();
  if (mimeType.includes('pdf') || filename.endsWith('.pdf')) return 'View PDF';
  if (mimeType.startsWith('image/') || /\.(jpe?g)$/.test(filename)) return 'View Image';
  return 'View Document';
};

interface AREDocumentPreviewState {
  areId: number;
  filename: string;
  objectUrl?: string;
  contentType?: string;
  loading: boolean;
  error?: string;
}

const issuanceOfficerLabel = (officer: any): string =>
  `${officerName(officer)} - ${value(officer.badge_number, 'No badge')} - ${value(officer.rank, 'No rank')} - ${value(officer.department_name || officer.unit_assignment, 'No department')} - ${statusText(officer.ltopf_status || 'valid')}`;

const officerSearchText = (officer: any): string =>
  [
    officerName(officer),
    officer.badge_number,
    officer.rank,
    officer.unit_assignment,
    officer.department_name,
  ].map((part) => value(part, '')).join(' ').toLowerCase();

const isEligibleIssuanceOfficer = (officer: any): boolean => {
  const employment = normalized(officer.employment_status);
  if (employment !== 'active') return false;
  const status = normalized(officer.status);
  if (status && status !== 'active') return false;
  if (typeof officer.ltopf_is_expired === 'boolean' && officer.ltopf_is_expired) return false;
  if (['expired', 'missing_expiration'].includes(ltopfStatus(officer))) return false;
  if (officer.department_is_active === false) return false;
  return true;
};

const firearmSearchText = (firearm: Firearm): string =>
  [
    firearm.serial_number,
    firearm.article,
    firearm.model,
    firearm.description,
    firearm.status,
  ].map((part) => value(part, '')).join(' ').toLowerCase();

const issuanceFirearmRegistryLabel = (firearm: Firearm) => {
  const modelOrArticle = firearm.model || firearm.article || 'Firearm';
  return `${firearm.serial_number} - ${value(firearm.article)} / ${modelOrArticle} - ${canonicalStatusLabel(firearm)} - ${value(firearm.department_name || firearm.assigned_to_unit, 'No department')}`;
};

const officerInitialState: OfficerPayload = {
  first_name: '',
  middle_name: '',
  last_name: '',
  badge_number: '',
  rank: '',
  unit_assignment: '',
  department: '',
  ltopf_number: '',
  ltopf_expiration_date: '',
  employment_status: 'active',
};

export const AccountableDashboard: React.FC = () => {
  // FRS Dashboard + Personnel and Issuance Module: assigned firearms, ARE actions, returns, custody, recent activity.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [ares, setAres] = React.useState<ARE[]>([]);
  const [returns, setReturns] = React.useState<ReturnRecord[]>([]);
  const [custody, setCustody] = React.useState<any[]>([]);
  const [summary, setSummary] = React.useState<AccountableDashboardSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextIssuances, nextAres, nextReturns, nextCustody] = await Promise.all([
        issuanceService.getIssuances(adminFilter),
        issuanceService.getAres(adminFilter),
        inventoryService.getReturnRecords(adminFilter),
        inventoryService.getCustodyLogs(adminFilter),
      ]);
      setSummary(await dashboardService.getAccountableSummary(adminFilter));
      setIssuances(nextIssuances);
      setAres(nextAres);
      setReturns(nextReturns);
      setCustody(nextCustody);
    } catch {
      setError('Accountable dashboard data is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [adminFilter]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading) return <div className="loading-state"><span className="loading-mark" />Loading accountable dashboard...</div>;
  if (error) return <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />;

  const activeIssuances = issuances.filter((item) => !item.returned_at && item.status !== 'returned');
  const pendingAres = ares.filter((are) => !isAreUploaded(are));
  const pendingReturns = returns.filter((record) => !record.is_successful && record.status !== 'completed');
  const hasAccountableRecords = issuances.length > 0 || ares.length > 0 || returns.length > 0 || custody.length > 0;

  const metrics = [
    ['Assigned firearm summary', summary?.assigned_firearm_summary?.current_assignments ?? activeIssuances.length, Package, 'blue'],
    ['Pending ARE uploads', summary?.pending_acknowledgments ?? pendingAres.length, FileUp, 'purple'],
    ['Return reminders', summary?.return_reminders?.length ?? pendingReturns.length, Undo2, 'neutral'],
    ['Compliance alerts', summary?.compliance_alerts?.length ?? 0, Bell, 'green'],
  ] as const;
  const recentActivity = issuances.slice(0, 5);

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div>
          <h1>Accountable Dashboard</h1>
          <p>Role-limited accountable officer workspace for assigned custody and ARE actions.</p>
        </div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} />Refresh data</button>
      </div>
      <FRSNote>FRS Dashboard Module and Personnel and Issuance Module: assigned firearms, pending ARE uploads, returns, custody summary, and recent accountable activity.</FRSNote>
      <div className="stats-grid inspector-stats">
        {metrics.map(([label, metric, Icon, tone]) => (
          <article className="stat-card" key={label}>
            <span className={`metric-icon ${tone}`}><Icon size={20} /></span>
            <div>
              <span className="stat-label">{label}</span>
              <strong className="stat-number">{metric}</strong>
              <span className="stat-note">Your records</span>
            </div>
          </article>
        ))}
      </div>
      <RecordsTableCard
        title="Recent accountable activity"
        description="Latest accountable issuance records for custody review."
        meta={`${recentActivity.length} recent`}
      >
        <table className="records-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Activity Type</th>
              <th>Firearm Serial</th>
              <th>Officer / User</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {recentActivity.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyTableState
                    title={hasAccountableRecords ? 'No recent activity' : 'No accountable records'}
                    description={hasAccountableRecords
                      ? 'No recent accountable issuance records were returned.'
                      : 'No assigned firearms, ARE actions, returns, or custody records were returned.'}
                  />
                </td>
              </tr>
            ) : recentActivity.map((issuance) => (
              <tr key={issuance.id}>
                <td>{formatDate(issuance.issued_at || issuance.requested_at)}</td>
                <td><TypeBadge type="issuance" tone="blue" /></td>
                <td>{value(issuance.firearm_serial)}</td>
                <td>{value(issuance.officer_name || issuance.officer_badge || issuance.requested_by_name)}</td>
                <td><StatusBadge status={issuance.status_display || issuance.status} label={statusText(issuance.status_display || issuance.status)} /></td>
                <td><span className="records-table-muted">Read-only</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>
    </div>
  );
};

export const AccountableInventoryLookup: React.FC = () => {
  // FRS Firearm Management Module: read-only firearm lookup for accountable workflows.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const [firearms, setFirearms] = React.useState<Firearm[]>([]);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selectedDetails, setSelectedDetails] = React.useState<Firearm | null>(null);
  const pageSize = 8;
  const { options: statusOptions } = useReferenceOptions('firearm_statuses');

  React.useEffect(() => {
    setLoading(true);
    inventoryService.getFirearms(withAdminDepartmentParam({ search, status: statusFilter || undefined }, adminDepartmentId))
      .then(setFirearms)
      .catch(() => setError('Inventory lookup is temporarily unavailable.'))
      .finally(() => setLoading(false));
  }, [adminDepartmentId, search, statusFilter]);

  const filteredFirearms = firearms;
  const visible = filteredFirearms.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(filteredFirearms.length / pageSize));

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Inventory Lookup</h1><p>Read-only firearm reference for accountable custody actions.</p></div></div>
      <FRSNote>FRS Firearm Management Module: search, status display, and assigned officer display only. Add/edit/delete are intentionally hidden.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Department firearms"
        description="Search and review firearm records without changing custody data."
        meta={`${filteredFirearms.length} record${filteredFirearms.length === 1 ? '' : 's'}`}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="serial, model, officer"
            onSearchChange={(value) => { setSearch(value); setPage(1); }}
            resultCount={loading ? 'Loading records' : `Showing ${visible.length} of ${filteredFirearms.length}`}
            filters={(
              <>
                <select
                  aria-label="Status filter"
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </>
            )}
          />
        )}
        footer={!loading && totalPages > 1 ? (
          <div className="pagination-row">
            <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        ) : null}
      >
        {loading ? <div className="loading-state"><span className="loading-mark" />Loading inventory...</div> : visible.length === 0 ? (
          <EmptyTableState title="No firearm records" description="No firearm records matched the current lookup." />
        ) : (
          <>
            <table className="records-table"><thead><tr><th>Serial</th><th>Description / Model</th><th>Status</th><th>Assigned officer</th><th>Action</th></tr></thead><tbody>
              {visible.map((firearm) => (
                <tr key={firearm.id}><td>{firearm.serial_number}</td><td>{value(firearm.description)} / {value(firearm.model)}</td><td><StatusBadge status={canonicalStatusKey(firearm)} label={canonicalStatusLabel(firearm)} /></td><td>{assignedText(firearm)}</td><td><button className="btn btn-secondary" type="button" onClick={() => setSelectedDetails(firearm)}>View Details</button></td></tr>
              ))}
            </tbody></table>
          </>
        )}
      </RecordsTableCard>
      {selectedDetails && <DetailDrawer open title="Department Firearm Details" subtitle={selectedDetails.serial_number} className="firearm-detail-drawer" onClose={() => setSelectedDetails(null)}>
        <div className="firearm-detail-layout">
          <div className="detail-grid">
            <div><span>Serial number</span><strong>{selectedDetails.serial_number}</strong></div>
            <div><span>Article</span><strong>{value(selectedDetails.article)}</strong></div>
            <div className="detail-field-wide"><span>Description / Make</span><strong>{value(selectedDetails.description)}</strong></div>
            <div><span>Model</span><strong>{value(selectedDetails.model)}</strong></div>
            <div><span>Caliber</span><strong>{value(selectedDetails.caliber)}</strong></div>
            <div><span>Acquisition date</span><strong>{formatDate(selectedDetails.acquisition_date)}</strong></div>
            <div><span>Status</span><strong>{statusText(selectedDetails.status)}</strong></div>
            <div className="detail-field-wide"><span>Assigned officer</span><strong>{assignedText(selectedDetails)}</strong></div>
            <div className="detail-field-wide"><span>Department</span><strong>{value(selectedDetails.department_name)}</strong></div>
          </div>
          <FirearmMasterImagePanel image={selectedDetails.primary_image} firearmSerialNumber={selectedDetails.serial_number} />
        </div>
      </DetailDrawer>}
    </div>
  );
};

export const MyAssignedFirearms: React.FC = () => {
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [ares, setAres] = React.useState<ARE[]>([]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextIssuances, nextAres] = await Promise.all([
        issuanceService.getIssuances(adminFilter),
        issuanceService.getAres(adminFilter),
      ]);
      setIssuances(nextIssuances);
      setAres(nextAres);
    } catch {
      setError('Assigned firearm records are temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [adminFilter]);

  React.useEffect(() => { void load(); }, [load]);

  const activeIssuances = issuances.filter((issuance) => !issuance.returned_at && issuance.status !== 'returned');
  const pendingUploads = ares.filter((are) => !isAreUploaded(are));
  const history = issuances.filter((issuance) => issuance.returned_at || issuance.status === 'returned');

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div><h1>My Assigned Firearms</h1><p>Assigned firearms, ARE information, pending uploads, and assignment history.</p></div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} />Refresh</button>
      </div>
      <FRSNote>Pending ARE uploads, current assignments, and assignment history are handled here. No standalone acknowledgment or ARE signing page is exposed in navigation.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />}

      {loading ? <div className="loading-state"><span className="loading-mark" />Loading assigned firearms...</div> : (
        <div className="section-stack">
          <CollapsibleSection title="Pending ARE Uploads" meta={pendingUploads.length} defaultOpen>
            {pendingUploads.length === 0 ? <EmptyState title="No pending ARE uploads" copy="No assigned firearm ARE uploads are pending." /> : (
              <div className="table-wrap"><table><thead><tr><th>Firearm</th><th>Officer</th><th>ARE Status</th><th>Document</th><th>Action</th></tr></thead><tbody>
                {pendingUploads.map((are) => <tr key={are.id}><td>{value(are.firearm_serial_number)}</td><td>{value(are.officer_name || are.officer_badge_number)}</td><td>{areUploadStatusLabel(are)}</td><td>{value(are.document_original_name || are.scanned_document, 'No document')}</td><td><span className="records-table-muted">Use ARE Upload</span></td></tr>)}
              </tbody></table></div>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Current Assignments" meta={activeIssuances.length} defaultOpen>
            {activeIssuances.length === 0 ? <EmptyState title="No current assignments" copy="No active assigned firearms were returned." /> : (
              <div className="table-wrap"><table><thead><tr><th>Firearm</th><th>Officer</th><th>Status</th><th>Issued</th><th>ARE</th></tr></thead><tbody>
                {activeIssuances.map((issuance) => <tr key={issuance.id}><td>{value(issuance.firearm_serial)}</td><td>{value(issuance.officer_name || issuance.officer_badge)}</td><td>{statusText(issuance.status_display || issuance.status)}</td><td>{formatDate(issuance.issued_at || issuance.requested_at)}</td><td>{issuance.are_record ? areUploadStatusLabel(issuance.are_record) : 'No ARE'}</td></tr>)}
              </tbody></table></div>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Assignment History" meta={history.length}>
            {history.length === 0 ? <EmptyState title="No assignment history" copy="Returned or closed assignment records will appear here." /> : (
              <div className="table-wrap"><table><thead><tr><th>Firearm</th><th>Officer</th><th>Status</th><th>Returned</th></tr></thead><tbody>
                {history.map((issuance) => <tr key={issuance.id}><td>{value(issuance.firearm_serial)}</td><td>{value(issuance.officer_name || issuance.officer_badge)}</td><td>{statusText(issuance.status_display || issuance.status)}</td><td>{formatDate(issuance.returned_at)}</td></tr>)}
              </tbody></table></div>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="ARE Information" meta={ares.length}>
            <AREStatusTable ares={ares} embedded />
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
};

export const OfficerLookup: React.FC = () => {
  // FRS Personnel and Issuance Module: department-scoped officer registry.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canManage = has('officer.registry.manage');
  const [officers, setOfficers] = React.useState<any[]>([]);
  const [departments, setDepartments] = React.useState<DepartmentSummary[]>([]);
  const [profileUser, setProfileUser] = React.useState<User | null>(null);
  const [form, setForm] = React.useState<OfficerPayload>(officerInitialState);
  const [editing, setEditing] = React.useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [employmentFilter, setEmploymentFilter] = React.useState('');
  const [ltopfFilter, setLtopfFilter] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [officerActionId, setOfficerActionId] = React.useState<number | null>(null);
  const { options: ltopfStatusOptions } = useReferenceOptions('ltopf_statuses');

  const load = React.useCallback(async () => {
    const [nextOfficers, nextDepartments, profile] = await Promise.all([
      inventoryService.getOfficers(adminFilter),
      inventoryService.getDepartments(),
      authService.getProfile().catch(() => null),
    ]);
    setOfficers(nextOfficers);
    setDepartments(nextDepartments.filter((department) => department.is_active !== false));
    setProfileUser(profile?.user || null);
  }, [adminFilter]);

  React.useEffect(() => { void load().catch(() => setError('Officer registry is temporarily unavailable.')); }, [load]);

  const departmentChoices = React.useMemo(() => {
    const activeDepartments = departments.filter((department) => department.is_active !== false);
    if (!profileUser?.is_department_scoped) return activeDepartments;
    const scopedIds = new Set((profileUser.departments || []).filter((department) => department.is_active !== false).map((department) => department.id));
    const scopedDepartments = activeDepartments.filter((department) => scopedIds.has(department.id));
    return scopedDepartments.length > 0 ? scopedDepartments : (profileUser.departments || []).filter((department) => department.is_active !== false);
  }, [departments, profileUser]);

  const defaultDepartment = React.useCallback(() => departmentChoices[0]?.id || '', [departmentChoices]);
  const departmentUnitAssignment = (department?: DepartmentSummary): string =>
    department?.name || department?.code || '';

  React.useEffect(() => {
    if (drawerOpen && !editing && !form.department) {
      const department = defaultDepartment();
      if (department) setForm((current) => ({ ...current, department }));
    }
  }, [defaultDepartment, drawerOpen, editing, form.department]);

  const visible = React.useMemo(() => officers.filter((officer) => {
    const departmentName = officer.department_name || officer.unit_assignment || '';
    const query = `${officerName(officer)} ${officer.badge_number || ''} ${officer.unit_assignment || ''} ${departmentName}`
      .toLowerCase();
    return query.includes(search.toLowerCase())
      && (!employmentFilter || officer.employment_status === employmentFilter)
      && (!ltopfFilter || ltopfStatus(officer) === ltopfFilter);
  }), [employmentFilter, ltopfFilter, officers, search]);
  const allowedEmploymentOptions = employmentEditOptions(editing ? employmentStatus(editing) : undefined);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...officerInitialState, department: defaultDepartment() });
    setMessage('');
    setError('');
    setDrawerOpen(true);
  };

  const edit = (officer: any) => {
    setEditing(officer);
    setForm({
      first_name: officer.first_name || '',
      middle_name: officer.middle_name || '',
      last_name: officer.last_name || '',
      badge_number: officer.badge_number || '',
      rank: officer.rank || '',
      unit_assignment: officer.department_name || officer.unit_assignment || '',
      department: officer.department || defaultDepartment(),
      ltopf_number: officer.ltopf_number || '',
      ltopf_expiration_date: officer.ltopf_expiration_date || '',
      employment_status: officer.employment_status || 'active',
    });
    setMessage('');
    setError('');
    setDrawerOpen(true);
  };

  const resetForm = () => {
    setEditing(null);
    setForm(officerInitialState);
    setDrawerOpen(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.first_name.trim() || !form.middle_name.trim() || !form.last_name.trim() || !form.badge_number.trim()) {
      setError('Officer name and badge number are required.');
      return;
    }
    if (!form.department) {
      setError('Department assignment is required.');
      return;
    }
    const department = departmentChoices.find((choice) => String(choice.id) === String(form.department));
    if (!department) {
      setError('Select an active department assignment.');
      return;
    }
    const originalExpiration = editing?.ltopf_expiration_date || '';
    const expirationChanged = !editing || form.ltopf_expiration_date !== originalExpiration;
    if (expirationChanged && form.ltopf_expiration_date && form.ltopf_expiration_date < today()) {
      setError('LTOPF expiration date cannot be earlier than today for a new or renewed record.');
      return;
    }
    try {
      const payload: OfficerPayload = {
        ...form,
        department: Number(form.department),
        unit_assignment: departmentUnitAssignment(department),
      };
      if (editing) {
        const updatePayload = Object.fromEntries(
          Object.entries(payload).filter(([field, fieldValue]) => {
            if (field === 'unit_assignment') {
              return fieldValue !== (editing.department_name || editing.unit_assignment || '');
            }
            return fieldValue !== (editing[field] ?? '');
          })
        ) as Partial<OfficerPayload>;
        await inventoryService.updateOfficer(editing.id, updatePayload);
      } else {
        await inventoryService.createOfficer(payload);
      }
      setMessage(editing ? 'Officer record updated.' : 'Officer record created.');
      resetForm();
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Officer record could not be saved.'));
    }
  };

  const setActivation = async (officer: any, active: boolean) => {
    const verb = active ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${active ? 'Reactivate' : 'Deactivate'} this officer?`)) return;
    setOfficerActionId(officer.id);
    setError('');
    setMessage('');
    try {
      await inventoryService.setOfficerActivation(officer.id, active);
      setMessage(active ? 'Officer record reactivated.' : 'Officer record deactivated.');
      await load();
    } catch (err: any) {
      setError(errorText(err, `Officer record could not be ${verb}d.`));
    } finally {
      setOfficerActionId(null);
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div><h1>Officer Registry</h1><p>Department officer records for issuance eligibility.</p></div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} />Refresh</button>
      </div>
      <FRSNote>FRS Personnel and Issuance Module: department-scoped officer registry with backend validation.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Officer registry"
        description="Search, filter, and maintain department officer records."
        meta={`${visible.length} record${visible.length === 1 ? '' : 's'}`}
        actions={canManage ? <button className="btn btn-primary" type="button" onClick={openCreate}>+ Create Officer</button> : null}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="name, badge, department"
            onSearchChange={setSearch}
            resultCount={`Showing ${visible.length} of ${officers.length}`}
            filters={(
              <>
                <select aria-label="Employment filter" value={employmentFilter} onChange={(event) => setEmploymentFilter(event.target.value)}>
                  <option value="">All employment</option>
                  {EMPLOYMENT_STATUS_OPTIONS.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
                <select aria-label="LTOPF status filter" value={ltopfFilter} onChange={(event) => setLtopfFilter(event.target.value)}>
                  <option value="">All LTOPF</option>
                  {ltopfStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
              </>
            )}
          />
        )}
      >
        <table className="records-table">
          <thead>
            <tr>
              <th>Officer</th>
              <th>Badge</th>
              <th>Department assignment</th>
              <th>LTOPF Expiration</th>
              <th>Employment</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyTableState title="No officer records" description="No officer records matched the current search and filters." />
                </td>
              </tr>
            ) : visible.map((officer) => {
              const currentLtopfStatus = ltopfStatus(officer);
              const currentEmploymentStatus = employmentStatus(officer);
              const workflowStatus = normalized(officer.status);
              const isActive = currentEmploymentStatus === 'active' && (!workflowStatus || workflowStatus === 'active');
              const canReactivate = currentEmploymentStatus === 'inactive' && workflowStatus === 'inactive';
              const departmentName = officer.department_name || officer.unit_assignment;
              return (
                <tr key={officer.id}>
                  <td>{officerName(officer)}</td>
                  <td>{value(officer.badge_number)}</td>
                  <td>{value(departmentName)}</td>
                  <td>
                    <span>{formatDate(officer.ltopf_expiration_date)}</span>
                    <StatusBadge status={currentLtopfStatus} label={officer.ltopf_expires_today ? 'Expires Today' : ltopfStatusLabel(currentLtopfStatus)} tone={ltopfTone(currentLtopfStatus)} />
                    {officer.ltopf_days_remaining != null && (
                      <span className="records-table-muted">{officer.ltopf_days_remaining} days remaining</span>
                    )}
                  </td>
                  <td><StatusBadge status={currentEmploymentStatus} label={employmentStatusLabel(currentEmploymentStatus)} tone={employmentTone(currentEmploymentStatus)} /></td>
                  <td>
                    {canManage ? (
                      <div className="table-actions">
                        <button className="btn btn-sm btn-secondary" type="button" onClick={() => edit(officer)}>Edit</button>
                        {isActive || canReactivate ? (
                          <button
                            className={isActive ? 'btn btn-sm btn-deactivate' : 'btn btn-sm btn-secondary'}
                            type="button"
                            disabled={officerActionId === officer.id}
                            onClick={() => setActivation(officer, !isActive)}
                          >
                            {officerActionId === officer.id ? 'Updating...' : isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        ) : null}
                      </div>
                    ) : <span className="records-table-muted">Read-only</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RecordsTableCard>
      {canManage && (
        <FormModal
          open={drawerOpen}
          title={editing ? 'Edit officer' : 'Create officer'}
          description="Officer registry fields are validated by the existing backend rules."
          onClose={resetForm}
        >
          <form className="inspector-form" onSubmit={submit}>
            <label>First name<input value={form.first_name} onChange={(event) => setForm({ ...form, first_name: event.target.value })} /></label>
            <label>Middle name<input value={form.middle_name} onChange={(event) => setForm({ ...form, middle_name: event.target.value })} /></label>
            <label>Last name<input value={form.last_name} onChange={(event) => setForm({ ...form, last_name: event.target.value })} /></label>
            <label>Badge number<input value={form.badge_number} onChange={(event) => setForm({ ...form, badge_number: event.target.value })} /></label>
            <label>Rank<input value={form.rank} onChange={(event) => setForm({ ...form, rank: event.target.value })} /></label>
            <label>Department assignment<select value={form.department || ''} onChange={(event) => setForm({ ...form, department: Number(event.target.value) || '' })}><option value="">Select department</option>{departmentChoices.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}</select></label>
            <label>LTOPF number<input value={form.ltopf_number} onChange={(event) => setForm({ ...form, ltopf_number: event.target.value })} /></label>
            <label>LTOPF expiration<input type="date" min={editing && form.ltopf_expiration_date === (editing.ltopf_expiration_date || '') && form.ltopf_expiration_date < today() ? undefined : today()} value={form.ltopf_expiration_date} onChange={(event) => setForm({ ...form, ltopf_expiration_date: event.target.value })} /></label>
            <label>
              Employment
              <select value={form.employment_status} onChange={(event) => setForm({ ...form, employment_status: event.target.value })}>
                {allowedEmploymentOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {editing && employmentStatus(editing) === 'inactive' ? (
                <small>Use Reactivate from the Officer Registry action.</small>
              ) : null}
            </label>
            <div className="form-actions full-width">
              <button className="btn btn-primary" type="submit">{editing ? 'Save officer' : 'Create officer'}</button>
              <button className="btn btn-secondary" type="button" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
};

export const OfficerRegistry = OfficerLookup;

export const AccountableIssuance: React.FC = () => {
  // FRS Personnel and Issuance Module: issuance creation only where backend/RBAC permits.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canCreate = has('firearm.issuance.create');
  const [firearms, setFirearms] = React.useState<Firearm[]>([]);
  const [officers, setOfficers] = React.useState<any[]>([]);
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [attempts, setAttempts] = React.useState<MultiIssuanceAttempt[]>([]);
  const [registryFirearms, setRegistryFirearms] = React.useState<Firearm[]>([]);
  const [attemptOpen, setAttemptOpen] = React.useState(false);
  const [attemptFirearm, setAttemptFirearm] = React.useState('');
  const [attemptOfficer, setAttemptOfficer] = React.useState('');
  const [attemptNotes, setAttemptNotes] = React.useState('');
  const [firearm, setFirearm] = React.useState('');
  const [officer, setOfficer] = React.useState('');
  const [firearmSearch, setFirearmSearch] = React.useState('');
  const [officerSearch, setOfficerSearch] = React.useState('');
  const [firearmDropdownOpen, setFirearmDropdownOpen] = React.useState(false);
  const [officerDropdownOpen, setOfficerDropdownOpen] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const [policyCheck, setPolicyCheck] = React.useState<Awaited<ReturnType<typeof issuanceService.checkIssuancePolicy>> | null>(null);
  const [authorityClaimed, setAuthorityClaimed] = React.useState(false);
  const [authorityReference, setAuthorityReference] = React.useState('');
  const [authorizationDocument, setAuthorizationDocument] = React.useState<File | null>(null);
  const [authorizationRemarks, setAuthorizationRemarks] = React.useState('');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [issuanceStatusFilter, setIssuanceStatusFilter] = React.useState('');
  const [recordGroup, setRecordGroup] = React.useState<'open' | 'history' | 'all'>('all');
  const [areStatusFilter, setAreStatusFilter] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [selectedMultiIssuance, setSelectedMultiIssuance] = React.useState<Issuance | null>(null);
  const [selectedDetailIssuance, setSelectedDetailIssuance] = React.useState<Issuance | null>(null);
  const [selectedDetailRepair, setSelectedDetailRepair] = React.useState<FirearmRepair | null>(null);
  const firearmDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const officerDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const { options: issuanceStatusOptions } = useReferenceOptions('issuance_statuses');
  const { options: areStatusOptions } = useReferenceOptions('are_statuses');

  const load = React.useCallback(async () => {
    const [nextFirearms, nextOfficers, nextIssuances, nextAttempts, nextIssuedFirearms] = await Promise.all([
      inventoryService.getEligibleIssuanceFirearms(adminFilter),
      inventoryService.getEligibleIssuanceOfficers(adminFilter),
      issuanceService.getIssuances({ ...adminFilter, group: recordGroup }),
      issuanceService.getMultiIssuanceAttempts(adminFilter),
      inventoryService.getFirearms(adminFilter),
    ]);
    setFirearms(nextFirearms || []);
    setOfficers(nextOfficers || []);
    setIssuances(nextIssuances || []);
    setAttempts(nextAttempts || []);
    setRegistryFirearms(nextIssuedFirearms || []);
  }, [adminFilter, recordGroup]);

  React.useEffect(() => { void load().catch(() => setError('Issuance data is temporarily unavailable.')); }, [load]);

  React.useEffect(() => {
    setSelectedDetailRepair(null);
    if (!selectedDetailIssuance?.repair_id) return;
    void repairService.getRepair(selectedDetailIssuance.repair_id)
      .then(setSelectedDetailRepair)
      .catch(() => setError('Repair details are temporarily unavailable.'));
  }, [selectedDetailIssuance]);

  React.useEffect(() => {
    setPolicyCheck(null);
    setAuthorityClaimed(false);
    setAuthorityReference('');
    setAuthorizationDocument(null);
    setAuthorizationRemarks('');
    if (!firearm || !officer) return;
    let active = true;
    void issuanceService.checkIssuancePolicy({
      firearm_id: Number(firearm),
      officer_id: Number(officer),
    }).then((next) => {
      if (active) setPolicyCheck(next);
    }).catch(() => {
      if (active) setPolicyCheck(null);
    });
    return () => { active = false; };
  }, [firearm, officer]);

  React.useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (firearmDropdownRef.current && !firearmDropdownRef.current.contains(target)) {
        setFirearmDropdownOpen(false);
      }
      if (officerDropdownRef.current && !officerDropdownRef.current.contains(target)) {
        setOfficerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const eligibleFirearms = firearms.filter((item) => {
    const supportedArticle = item.article ? isSupportedIssuanceArticle(item.article) : true;
    return item.is_supported_for_issuance !== false && supportedArticle && allowedIssuanceStatuses.includes(item.status);
  });
  const selectedFirearm = eligibleFirearms.find((item) => String(item.id) === firearm);
  const eligibleOfficers = officers.filter(isEligibleIssuanceOfficer);
  const selectedOfficer = eligibleOfficers.find((item) => String(item.id) === officer);
  const firearmQuery = selectedFirearm && firearmSearch === issuanceFirearmRegistryLabel(selectedFirearm) ? '' : firearmSearch.toLowerCase();
  const officerQuery = selectedOfficer && officerSearch === issuanceOfficerLabel(selectedOfficer) ? '' : officerSearch.toLowerCase();
  const visibleFirearmOptions = eligibleFirearms.filter((item) =>
    !firearmQuery || firearmSearchText(item).includes(firearmQuery)
  ).slice(0, 8);
  const visibleOfficerOptions = eligibleOfficers.filter((item) =>
    !officerQuery || officerSearchText(item).includes(officerQuery)
  ).slice(0, 8);
  const visibleIssuances = React.useMemo(() => issuances.filter((issuance) => {
    const issuanceStatus = issuanceDisplayLabel(issuance);
    const issuanceStatusGroup = issuance.status_group || issuance.status;
    const areStatus = shouldShowAreRequirement(issuance) ? canonicalAreStatus(issuance.are_record) : '';
    const officerNameValue = value(issuance.officer_name || issuance.officer_badge, '');
    const query = `${issuance.firearm_serial || ''} ${officerNameValue} ${issuanceStatus} ${areStatus}`.toLowerCase();
    return query.includes(search.toLowerCase())
      && (!issuanceStatusFilter || issuanceStatusGroup === issuanceStatusFilter)
      && (!areStatusFilter || areStatus === areStatusFilter);
  }), [areStatusFilter, issuanceStatusFilter, issuances, search]);
  const issuedFirearms = registryFirearms.filter((item) => item.status === 'issued');
  const firearmForIssuance = (issuance: Issuance): Firearm | undefined => registryFirearms.find(
    (item) => item.id === issuance.firearm || item.serial_number === issuance.firearm_serial
  );

  const resetIssuanceForm = () => {
    setFirearm('');
    setOfficer('');
    setFirearmSearch('');
    setOfficerSearch('');
    setFirearmDropdownOpen(false);
    setOfficerDropdownOpen(false);
    setNotes('');
    setPolicyCheck(null);
    setAuthorityClaimed(false);
    setAuthorityReference('');
    setAuthorizationDocument(null);
    setAuthorizationRemarks('');
    setDrawerOpen(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!firearm || !officer) {
      setError('Target firearm and officer are required.');
      return;
    }
    if (policyCheck?.multiple_handgun_review_required) {
      if (!authorityClaimed || !authorityReference.trim() || !authorizationDocument) {
        setError('Proper-authority confirmation, reference, and supporting document are required.');
        return;
      }
    }
    try {
      const trimmedNotes = notes.trim();
      await issuanceService.createIssuance({
        firearm: Number(firearm),
        officer: Number(officer),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        ...(policyCheck?.multiple_handgun_review_required ? {
          multiple_handgun_authority_claimed: authorityClaimed,
          authority_reference: authorityReference.trim(),
          authorization_document: authorizationDocument || undefined,
          ...(authorizationRemarks.trim() ? { authorization_remarks: authorizationRemarks.trim() } : {}),
        } : {}),
      });
      resetIssuanceForm();
      setError('');
      setMessage('Issuance request submitted successfully and sent to Logistics for review.');
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Issuance request could not be created. Backend eligibility validation remains authoritative.'));
      if (err?.response?.status === 409) await load();
    }
  };

  const submitAttempt = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!attemptFirearm || !attemptOfficer || !attemptNotes.trim()) {
      setMessage('');
      setError('Firearm, attempted officer, and remarks are required.');
      return;
    }
    try {
      const result = await issuanceService.recordMultiIssuanceAttempt({
        firearm: Number(attemptFirearm), attempted_officer: Number(attemptOfficer),
        notes: attemptNotes.trim(),
      });
      setAttemptOpen(false); setAttemptFirearm(''); setAttemptOfficer(''); setAttemptNotes('');
      setError('');
      setMessage(result.message || (result.duplicate
        ? `This Multi-Issuance attempt is already recorded under Case ${result.case}. No duplicate record was created.`
        : `Multi-Issuance attempt recorded. No custody transfer occurred. Case ${result.case} is ready for Logistics review.`));
      await load();
    } catch (err: any) {
      setMessage('');
      setError(errorText(err, 'Multi-issuance attempt could not be recorded.'));
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Issuance</h1><p>Create firearm issuance only where Accountable RBAC and backend rules permit.</p></div></div>
      <FRSNote>FRS Personnel and Issuance Module: backend enforces available in-storage firearm status, active officer status, and valid LTOPF.</FRSNote>
      <WorkflowFeedback message={message} onDismiss={() => setMessage('')} />
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Issuance records"
        description="Review issuance requests and ARE linkage before creating a new record."
        meta={`${visibleIssuances.length} record${visibleIssuances.length === 1 ? '' : 's'}`}
        actions={canCreate ? <button className="btn btn-primary" type="button" onClick={() => { setError(''); setMessage(''); setDrawerOpen(true); }}>+ Create Issuance</button> : null}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="firearm, officer, status"
            onSearchChange={setSearch}
            resultCount={`Showing ${visibleIssuances.length} of ${issuances.length}`}
            filters={(
              <>
                <select
                  aria-label="Issuance record group"
                  value={recordGroup}
                  onChange={(event) => setRecordGroup(event.target.value as typeof recordGroup)}
                >
                  <option value="open">Open Issuances</option>
                  <option value="history">Issuance History</option>
                  <option value="all">All Records</option>
                </select>
                <select aria-label="Issuance status filter" value={issuanceStatusFilter} onChange={(event) => setIssuanceStatusFilter(event.target.value)}>
                  <option value="">All issuance statuses</option>
                  {issuanceStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
                <select aria-label="ARE status filter" value={areStatusFilter} onChange={(event) => setAreStatusFilter(event.target.value)}>
                  <option value="">All ARE statuses</option>
                  {areStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
              </>
            )}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Issuance Reference</th><th>Firearm</th><th>Officer</th><th>Issuance Status</th><th>Active Assignment</th><th>ARE Status</th><th>Requested Date</th><th>Issued Date</th><th>Return / Closure Date</th><th>Action</th></tr></thead>
          <tbody>
            {visibleIssuances.length === 0 ? (
              <tr><td colSpan={10}><EmptyTableState title="No issuance records" description="No Accountable issuance records matched the current search and filters." /></td></tr>
            ) : visibleIssuances.map((issuance) => {
              const issuanceStatus = issuanceDisplayLabel(issuance);
              const multiDetected = hasMultiIssuance(issuance);
              const showAreRequirement = shouldShowAreRequirement(issuance);
              const areStatus = showAreRequirement ? (issuance.are_record ? areUploadStatusLabel(issuance.are_record) : 'No ARE') : '';
              const multiSummary = multiIssuanceSummary(issuance);
              return (
                <tr key={issuance.id}>
                  <td>{`ISS-${issuance.id}`}</td>
                  <td>{value(issuance.firearm_serial)}</td>
                  <td>{value(issuance.officer_name || issuance.officer_badge)}</td>
                  <td>
                    <StatusBadge status={issuance.status_group || issuance.status} label={issuanceStatus} />
                    {multiSummary ? <div className="form-helper-muted">{multiSummary}</div> : null}
                    {issuance.review_type && issuance.review_type !== 'none' ? <div className="form-helper-muted">{issuance.review_type_display || statusText(issuance.review_type)}</div> : null}
                  </td>
                  <td>
                    <StatusBadge
                      status={issuance.has_active_assignment ? 'active' : 'inactive'}
                      label={issuance.active_assignment_label || (issuance.has_active_assignment ? 'Active' : 'Inactive')}
                      tone={issuance.has_active_assignment ? 'success' : 'neutral'}
                    />
                  </td>
                  <td>{areStatus ? <StatusBadge status={issuance.are_record ? areUploadStatus(issuance.are_record) : 'missing'} label={areStatus} tone={issuance.are_record ? areUploadTone(issuance.are_record) : 'warning'} /> : ''}</td>
                  <td>{formatDate(issuance.requested_at)}</td>
                  <td>{formatDate(issuance.issued_at)}</td>
                  <td>{formatDate(issuance.return_or_closure_date || issuance.returned_at)}</td>
                  <td>
                    <div className="table-actions">
                      {multiDetected ? <button className="btn btn-secondary" type="button" onClick={() => setSelectedMultiIssuance(issuance)}>View Details</button> : null}
                      {!multiDetected ? <button className="btn btn-secondary" type="button" onClick={() => setSelectedDetailIssuance(issuance)}>View Details</button> : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RecordsTableCard>
      <RecordsTableCard
        title="Multi-Issuance Attempts"
        description="Recorded custody-conflict attempts are audit events and do not transfer custody or create an ARE."
        meta={`${attempts.length} attempt${attempts.length === 1 ? '' : 's'}`}
        actions={has('firearm.multi_issuance.record') || canCreate ? (
          <button className="btn btn-secondary" type="button" onClick={() => { setError(''); setMessage(''); setAttemptOpen(true); }}>Record Multi-Issuance Attempt</button>
        ) : null}
      >
        <table className="records-table">
          <thead><tr><th>Case</th><th>Firearm</th><th>Current Custodian</th><th>Attempted Officer</th><th>Attempt Date</th><th>Status</th></tr></thead>
          <tbody>{attempts.length ? attempts.map((attempt) => (
            <tr key={attempt.id}><td>{attempt.case}</td><td>{attempt.firearm_serial}</td><td>{attempt.current_custodian}</td>
              <td>{attempt.attempted_officer_name}</td><td>{formatDate(attempt.attempted_at)}</td>
              <td><StatusBadge status={attempt.disposition} label={statusText(attempt.disposition)} /></td></tr>
          )) : <tr><td colSpan={6}><EmptyTableState title="No conflict attempts" description="No custody-conflict attempts have been recorded." /></td></tr>}</tbody>
        </table>
      </RecordsTableCard>
      {attemptOpen && <FormModal open title="Record Multi-Issuance Attempt" description="No custody transfer or ARE will be created." onClose={() => setAttemptOpen(false)}>
        <form className="inspector-form" onSubmit={submitAttempt}>
          <label>Currently issued firearm<select value={attemptFirearm} onChange={(e) => setAttemptFirearm(e.target.value)} required>
            <option value="">Select firearm</option>{issuedFirearms.map((item) => <option key={item.id} value={item.id}>{item.serial_number} — {item.assigned_to_name || 'Current custodian'}</option>)}
          </select></label>
          <label>Attempted target officer<select value={attemptOfficer} onChange={(e) => setAttemptOfficer(e.target.value)} required>
            <option value="">Select officer</option>{eligibleOfficers.map((item) => <option key={item.id} value={item.id}>{issuanceOfficerLabel(item)}</option>)}
          </select></label>
          <label className="full-width">Remarks<textarea value={attemptNotes} onChange={(e) => setAttemptNotes(e.target.value)} required rows={4} /></label>
          <div className="form-helper-muted full-width">This records a reported custody-conflict attempt for Logistics review. The firearm remains with its current authoritative custodian.</div>
          <div className="form-actions full-width"><button className="btn btn-primary" type="submit">Record attempt</button><button className="btn btn-secondary" type="button" onClick={() => setAttemptOpen(false)}>Cancel</button></div>
        </form>
      </FormModal>}
      {selectedMultiIssuance && (
        <DetailDrawer
          open={Boolean(selectedMultiIssuance)}
          title="Conflict — Multi-Issuance Details"
          subtitle={value(selectedMultiIssuance.firearm_serial)}
          meta={<StatusBadge status={selectedMultiIssuance.status} label={statusText(selectedMultiIssuance.status_display || selectedMultiIssuance.status)} />}
          onClose={() => setSelectedMultiIssuance(null)}
          closeLabel="Close Multi-Issuance details"
        >
          {selectedMultiIssuance.status === 'rejected' && selectedMultiIssuance.multi_issuance?.resolved ? (
            <div className="full-width custody-summary"><span>Summary</span><p>This request was rejected after Logistics resolved a Multi-Issuance conflict.</p></div>
          ) : null}
          <div className="detail-grid">
            {multiIssuanceDetailRows(selectedMultiIssuance).map(([label, detail]) => (
              <div key={label}><span>{label}</span><strong>{detail}</strong></div>
            ))}
          </div>
        </DetailDrawer>
      )}
      {selectedDetailIssuance && (
        <FirearmIssuanceDetailsDrawer
          issuance={selectedDetailIssuance}
          firearm={firearmForIssuance(selectedDetailIssuance)}
          repair={selectedDetailRepair}
          onClose={() => { setSelectedDetailIssuance(null); setSelectedDetailRepair(null); }}
        />
      )}
      {canCreate && (
        <FormModal
          open={drawerOpen}
          title="Create Issuance"
          description="Create an issuance using existing backend eligibility rules."
          onClose={resetIssuanceForm}
        >
          <form className="inspector-form" onSubmit={submit}>
            <div className="officer-autocomplete" ref={firearmDropdownRef}>
              <label htmlFor="issuance-firearm-search">Firearm serial number</label>
              <div className="autocomplete-field">
                <input
                  id="issuance-firearm-search"
                  aria-label="Firearm serial number"
                  value={firearmSearch}
                  placeholder="Search serial, model, article, or status"
                  onClick={() => {
                    setOfficerDropdownOpen(false);
                    setFirearmDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setOfficerDropdownOpen(false);
                    setFirearmDropdownOpen(true);
                  }}
                  onChange={(event) => {
                    setFirearmSearch(event.target.value);
                    setFirearm('');
                    setOfficerDropdownOpen(false);
                    setFirearmDropdownOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape' && firearmDropdownOpen) {
                      event.stopPropagation();
                      setFirearmDropdownOpen(false);
                    }
                  }}
                />
                <button
                  className="autocomplete-toggle"
                  type="button"
                  aria-label="Open firearm options"
                title="Open firearm options"
                onClick={() => {
                  setOfficerDropdownOpen(false);
                  setFirearmDropdownOpen(true);
                }}
                >
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              </div>
              {firearmDropdownOpen ? (
                <div className="officer-autocomplete-list" role="listbox" aria-label="Firearm suggestions">
                  {eligibleFirearms.length === 0 ? (
                    <div className="autocomplete-empty">No eligible firearms found. Firearms must be supported and department-active.</div>
                  ) : visibleFirearmOptions.length === 0 ? (
                    <div className="autocomplete-empty">No matching firearms.</div>
                  ) : visibleFirearmOptions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={String(item.id) === firearm}
                      onClick={() => {
                        setFirearm(String(item.id));
                        setFirearmSearch(issuanceFirearmRegistryLabel(item));
                        setFirearmDropdownOpen(false);
                      }}
                    >
                      {issuanceFirearmRegistryLabel(item)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {selectedFirearm ? (
              <div className="issuance-selection-summary full-width" role="group" aria-label="Selected firearm summary">
                <div><span>Serial Number</span><strong>{value(selectedFirearm.serial_number)}</strong></div>
                <div><span>Model/Article</span><strong>{value(selectedFirearm.model || selectedFirearm.article)}{selectedFirearm.model && selectedFirearm.article ? ` / ${selectedFirearm.article}` : ''}</strong></div>
                <div><span>Current Status</span><strong>{statusText(selectedFirearm.status)}</strong></div>
              </div>
            ) : null}
            <div className="officer-autocomplete" ref={officerDropdownRef}>
              <label htmlFor="issuance-officer-search">Officer</label>
              <div className="autocomplete-field">
                <input
                  id="issuance-officer-search"
                  aria-label="Officer"
                  value={officerSearch}
                  placeholder="Search name, badge, rank, or department"
                  onClick={() => {
                    setFirearmDropdownOpen(false);
                    setOfficerDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setFirearmDropdownOpen(false);
                    setOfficerDropdownOpen(true);
                  }}
                  onChange={(event) => {
                    setOfficerSearch(event.target.value);
                    setOfficer('');
                    setFirearmDropdownOpen(false);
                    setOfficerDropdownOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape' && officerDropdownOpen) {
                      event.stopPropagation();
                      setOfficerDropdownOpen(false);
                    }
                  }}
                />
                <button
                  className="autocomplete-toggle"
                  type="button"
                  aria-label="Open officer options"
                title="Open officer options"
                onClick={() => {
                  setFirearmDropdownOpen(false);
                  setOfficerDropdownOpen(true);
                }}
                >
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              </div>
              {officerDropdownOpen ? (
                <div className="officer-autocomplete-list" role="listbox" aria-label="Officer suggestions">
                  {eligibleOfficers.length === 0 ? (
                    <div className="autocomplete-empty">No eligible officers found. Officers must be active with valid LTOPF.</div>
                  ) : visibleOfficerOptions.length === 0 ? (
                    <div className="autocomplete-empty">No matching officers.</div>
                  ) : visibleOfficerOptions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={String(item.id) === officer}
                      onClick={() => {
                        setOfficer(String(item.id));
                        setOfficerSearch(issuanceOfficerLabel(item));
                        setOfficerDropdownOpen(false);
                      }}
                    >
                      {issuanceOfficerLabel(item)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <label className="full-width">Notes / remarks<textarea value={notes} placeholder="Optional issuance remarks" onChange={(event) => setNotes(event.target.value)} /></label>
            {policyCheck?.same_firearm_conflict ? (
              <div className="form-helper-muted full-width">The selected firearm has active custody. Submission will be recorded as a same-firearm custody conflict.</div>
            ) : null}
            {policyCheck?.multiple_handgun_review_required ? (
              <div className="full-width issuance-selection-summary" role="group" aria-label="Multiple handgun authorization">
                <div><span>Review Type</span><strong>Multiple-Handgun Policy Review</strong></div>
                <div><span>Existing active handguns</span><strong>{policyCheck.existing_active_handguns.map((item) => item.firearm_serial_number).join(', ')}</strong></div>
                <label className="full-width checkbox-row"><input type="checkbox" checked={authorityClaimed} onChange={(event) => setAuthorityClaimed(event.target.checked)} /> Proper authority exists</label>
                <label className="full-width">Authority reference<input value={authorityReference} onChange={(event) => setAuthorityReference(event.target.value)} required={authorityClaimed} /></label>
                <label className="full-width">Supporting document<input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(event) => setAuthorizationDocument(event.target.files?.[0] || null)} required={authorityClaimed} /></label>
                <label className="full-width">Authorization remarks<textarea value={authorizationRemarks} onChange={(event) => setAuthorizationRemarks(event.target.value)} /></label>
              </div>
            ) : null}
            <div className="form-actions full-width">
              <button className="btn btn-primary" type="submit"><ClipboardList size={14} />Create issuance</button>
              <button className="btn btn-secondary" type="button" onClick={resetIssuanceForm}>Cancel</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
};

export const AREUpload: React.FC = () => {
  // FRS Personnel and Issuance Module: approved issuance ARE upload using existing backend endpoint.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canUpload = has('firearm.are.upload');
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [selected, setSelected] = React.useState('');
  const [expirationDate, setExpirationDate] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [documentPreview, setDocumentPreview] = React.useState<AREDocumentPreviewState | null>(null);

  const load = React.useCallback(async () => setIssuances(await issuanceService.getIssuances(adminFilter)), [adminFilter]);
  React.useEffect(() => { void load().catch(() => setError('ARE upload records are temporarily unavailable.')); }, [load]);

  const areStatusIssuances = React.useMemo(() => issuances.filter((issuance) => {
    const status = normalized(issuance.status);
    return Boolean(issuance.are_record)
      && shouldShowAreRequirement(issuance)
      && ['issued', 'completed'].includes(status);
  }), [issuances]);
  const uploadIssuances = React.useMemo(() => areStatusIssuances.filter((issuance) =>
    normalized(issuance.status) === 'issued'
      && Boolean(issuance.are_record)
      && ['pending_accountable_are', 'missing_expiration'].includes(areUploadStatus(issuance.are_record))
  ), [areStatusIssuances]);
  const selectedIssuance = uploadIssuances.find((issuance) => String(issuance.are_record?.id) === selected);

  React.useEffect(() => {
    if (selected && !uploadIssuances.some((issuance) => String(issuance.are_record?.id) === selected)) {
      setSelected('');
      return;
    }
    if (!selected && uploadIssuances.length === 1) {
      setSelected(String(uploadIssuances[0].are_record!.id));
    }
  }, [selected, uploadIssuances]);

  const closeDocumentPreview = React.useCallback(() => {
    setDocumentPreview((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
      return null;
    });
  }, []);

  React.useEffect(() => closeDocumentPreview, [closeDocumentPreview]);

  const openDocumentPreview = async (are: ARE) => {
    const filename = areDocumentName(are) || `ARE-${are.id} document`;
    setDocumentPreview((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
      return { areId: are.id, filename, loading: true };
    });
    try {
      const blob = await issuanceService.getAreDocument(are.id);
      const objectUrl = URL.createObjectURL(blob);
      setDocumentPreview((current) => {
        if (!current || current.areId !== are.id) {
          URL.revokeObjectURL(objectUrl);
          return current;
        }
        if (current.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return { ...current, objectUrl, contentType: blob.type || are.document_mime_type || undefined, loading: false, error: undefined };
      });
    } catch (err: any) {
      setDocumentPreview((current) => {
        if (!current || current.areId !== are.id) return current;
        return { ...current, loading: false, error: errorText(err, 'ARE document preview could not be loaded.') };
      });
    }
  };

  const chooseFile = (nextFile: File | null) => {
    setError('');
    if (!nextFile) {
      setFile(null);
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg'];
    if (!allowed.includes(nextFile.type)) {
      setError('Only PDF, JPG, and JPEG ARE documents are supported.');
      setFile(null);
      return;
    }
    if (nextFile.size > 5 * 1024 * 1024) {
      setError('ARE document must not exceed 5 MB.');
      setFile(null);
      return;
    }
    setFile(nextFile);
  };

  const upload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !file || !expirationDate) {
      setError('ARE record, expiration date, and document are required.');
      return;
    }
    if (expirationDate < today()) {
      setError('ARE expiration date cannot be earlier than today for a new or renewed record.');
      return;
    }
    const data = new FormData();
    data.append('document', file);
    data.append('expiration_date', expirationDate);
    try {
      await issuanceService.uploadAre(Number(selected), data);
      setMessage('ARE document uploaded.');
      setSelected('');
      setExpirationDate('');
      setFile(null);
      await load();
    } catch (err: any) {
      setMessage('');
      setError(errorText(err, 'ARE document could not be uploaded.'));
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>ARE Upload</h1><p>Upload authorized ARE documents for accountable records.</p></div></div>
      <FRSNote>FRS Personnel and Issuance Module: upload status, backend validation errors, current ARE status, and existing ARE visibility.</FRSNote>
      <WorkflowFeedback message={message} onDismiss={() => setMessage('')} />
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      {canUpload && (
        <form className="panel inspector-form" onSubmit={upload}>
          <label>ARE record<select value={selected} onChange={(event) => setSelected(event.target.value)}><option value="">Select ARE</option>{uploadIssuances.map((issuance) => <option key={issuance.are_record!.id} value={issuance.are_record!.id}>{value(issuance.firearm_serial || issuance.are_record!.firearm_serial_number)} - {areUploadStatusLabel(issuance.are_record)}</option>)}</select></label>
          <label>ARE expiration date<input aria-label="ARE expiration date" type="date" min={today()} value={expirationDate} onChange={(event) => setExpirationDate(event.target.value)} /></label>
          <label>ARE image and PDF<input aria-label="ARE image and PDF" type="file" accept=".pdf,.jpg,.jpeg" onChange={(event) => chooseFile(event.target.files?.[0] || null)} /></label>
          {selectedIssuance?.are_record ? <p className="form-helper-muted full-width">Selected firearm: {value(selectedIssuance.firearm_serial || selectedIssuance.are_record.firearm_serial_number)}. Supported files: PDF, JPG, or JPEG up to 5 MB.</p> : null}
          <button className="btn btn-primary" type="submit"><FileUp size={14} />Upload ARE</button>
        </form>
      )}
      <AREStatusTable issuances={areStatusIssuances} openDocumentPreview={openDocumentPreview} />
      <DocumentPreviewModal
        open={Boolean(documentPreview)}
        title={`ARE-${documentPreview?.areId || ''} Document Preview`}
        filename={documentPreview?.filename}
        objectUrl={documentPreview?.objectUrl}
        contentType={documentPreview?.contentType}
        loading={documentPreview?.loading}
        error={documentPreview?.error}
        onClose={closeDocumentPreview}
      />
    </div>
  );
};

export const AccountableARESigning = AREUpload;

const AREStatusTable: React.FC<{
  issuances?: Issuance[];
  ares?: ARE[];
  embedded?: boolean;
  openDocumentPreview?: (are: ARE) => void;
}> = ({ issuances, ares, embedded = false, openDocumentPreview }) => {
  const rows = issuances
    ? issuances.map((issuance) => ({ issuance, are: issuance.are_record! }))
    : (ares || []).map((are) => ({ issuance: null, are }));

  return (
    <section className={embedded ? 'embedded-panel' : 'panel'}>
      <h2>Current ARE upload status</h2>
      {rows.length === 0 ? <EmptyState title="No ARE records" copy="No issued ARE records were returned." /> : (
        <div className="table-wrap"><table><thead><tr><th>Firearm</th><th>Officer</th><th>Issuance Status</th><th>ARE Upload Status</th><th>ARE Expiration Status</th><th>ARE Expiration</th><th>Days Remaining</th><th>Uploaded Document</th><th>Action</th></tr></thead><tbody>
          {rows.map(({ issuance, are }) => (
            <tr key={are.id}>
              <td>{value(issuance?.firearm_serial || are.firearm_serial_number)}</td>
              <td>{value(issuance?.officer_name || are.officer_name || are.officer_badge_number)}</td>
              <td>{issuance ? <StatusBadge status={issuance.status} label={statusText(issuance.status)} /> : '-'}</td>
              <td><StatusBadge status={areUploadStatus(are)} label={areUploadStatusLabel(are)} tone={areUploadTone(are)} /></td>
              <td><StatusBadge status={are.expiration_status || 'missing_expiration'} label={areExpirationLabel(are)} tone={areExpirationTone(are)} /></td>
              <td>{formatDate(are.expiration_date)}</td>
              <td>{are.expiration_days_remaining == null ? '-' : are.expiration_days_remaining}</td>
              <td>
                {areDocumentName(are) ? (
                  openDocumentPreview ? (
                    <button className="document-preview-link" type="button" title={areDocumentName(are)} onClick={() => openDocumentPreview(are)}>
                      {areDocumentPreviewLabel(are)}
                    </button>
                  ) : areDocumentPreviewLabel(are)
                ) : 'No document'}
              </td>
              <td><span className="records-table-muted">{isAreUploaded(are) ? 'Uploaded' : 'Ready for upload'}</span></td>
            </tr>
          ))}
        </tbody></table></div>
      )}
    </section>
  );
};

export const AccountableReturns: React.FC = () => {
  // FRS Personnel and Issuance Module: normal return processing only where current RBAC allows it.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canReturn = has('firearm.return.manage') || has('firearm.return.confirm.accountable');
  const [issuances, setIssuances] = React.useState<Issuance[]>([]);
  const [records, setRecords] = React.useState<ReturnRecord[]>([]);
  const [selected, setSelected] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [condition, setCondition] = React.useState('');
  const [remarks, setRemarks] = React.useState('');
  const [evidenceFile, setEvidenceFile] = React.useState<File | null>(null);
  const [evidencePreviewUrl, setEvidencePreviewUrl] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [reasonFilter, setReasonFilter] = React.useState('');
  const { options: statusOptions } = useReferenceOptions('return_statuses');
  const { options: reasonOptions } = useReferenceOptions('return_reasons');
  const { options: conditionOptions } = useReferenceOptions('firearm_conditions');

  const load = React.useCallback(async () => {
    const [nextIssuances, nextRecords] = await Promise.all([
      issuanceService.getIssuances(adminFilter),
      inventoryService.getReturnRecords(adminFilter),
    ]);
    setIssuances(nextIssuances);
    setRecords(nextRecords);
  }, [adminFilter]);
  React.useEffect(() => { void load().catch(() => setError('Return records are temporarily unavailable.')); }, [load]);

  const visibleRecords = React.useMemo(() => records.filter((record) => {
    const status = record.status || record.action_type || '';
    const statusGroup = record.status_group || status;
    const returnReason = record.reason || record.action_type || '';
    const query = `${record.firearm_serial || ''} ${record.officer_name || ''} ${record.officer_badge || ''} ${status} ${returnReason} ${record.firearm_condition || ''}`.toLowerCase();
    return query.includes(search.toLowerCase())
      && (!statusFilter || statusGroup === statusFilter)
      && (!reasonFilter || referenceValue(returnReason) === reasonFilter);
  }), [reasonFilter, records, search, statusFilter]);
  const returnEligibleIssuances = React.useMemo(
    () => issuances.filter(isReturnEligibleIssuance),
    [issuances]
  );

  React.useEffect(() => {
    if (!evidenceFile) {
      setEvidencePreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(evidenceFile);
    setEvidencePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [evidenceFile]);

  const resetReturnForm = () => {
    setSelected('');
    setReason('');
    setCondition('');
    setRemarks('');
    setEvidenceFile(null);
    setDrawerOpen(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !reason.trim() || !condition || !evidenceFile) {
      setError('Issuance, return reason, firearm condition, and return-condition image are required.');
      return;
    }
    if (!FIREARM_EVIDENCE_ALLOWED_MIME_TYPES.includes(evidenceFile.type as typeof FIREARM_EVIDENCE_ALLOWED_MIME_TYPES[number])) {
      setError('Return-condition evidence must be a JPEG, PNG, or WebP image.');
      return;
    }
    if (evidenceFile.size <= 0 || evidenceFile.size > FIREARM_EVIDENCE_MAX_BYTES) {
      setError('Return-condition evidence must be non-empty and no larger than 5 MiB.');
      return;
    }
    setSubmitting(true);
    try {
      await issuanceService.returnIssuance(Number(selected), {
        reason,
        remarks,
        firearm_condition: condition as 'serviceable' | 'defective' | 'unserviceable',
        evidence_file: evidenceFile,
      });
      setMessage('Firearm return submitted.');
      resetReturnForm();
      setError('');
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Firearm return could not be submitted.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Return Requests</h1><p>Submit return requests, view request status, and review return history.</p></div></div>
      <FRSNote>FRS Personnel and Issuance Module: normal Accountable return workflow only. Logistics oversight/revocation actions are hidden.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Return history"
        description="Review return requests and submitted return records before starting a new return."
        meta={`${visibleRecords.length} record${visibleRecords.length === 1 ? '' : 's'}`}
        actions={canReturn ? <button className="btn btn-primary" type="button" onClick={() => { setError(''); setDrawerOpen(true); }}>+ Submit Return</button> : null}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="firearm, officer, condition"
            onSearchChange={setSearch}
            resultCount={`Showing ${visibleRecords.length} of ${records.length}`}
            filters={(
              <>
                <select aria-label="Return status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">All request statuses</option>
                  {statusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
                <select aria-label="Return reason filter" value={reasonFilter} onChange={(event) => setReasonFilter(event.target.value)}>
                  <option value="">All reasons</option>
                  {reasonOptions.map((returnReason) => <option key={returnReason.value} value={returnReason.value}>{returnReason.label}</option>)}
                </select>
              </>
            )}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Firearm</th><th>Officer</th><th>Type</th><th>Request Status</th><th>Reason</th><th>Firearm Condition</th><th>Submitted On</th><th>Confirmed On</th><th>Action</th></tr></thead>
          <tbody>
            {visibleRecords.length === 0 ? (
              <tr><td colSpan={9}><EmptyTableState title="No return history" description="No Accountable return records matched the current search and filters." /></td></tr>
            ) : visibleRecords.map((record) => {
              const requestType = record.action_type || '';
              const requestStatus = record.status || '';
              const returnReason = record.reason || '';
              return (
                <tr key={record.id}>
                  <td>{value(record.firearm_serial)}</td>
                  <td>{value(record.officer_name || record.officer_badge)}</td>
                  <td><TypeBadge type={requestType} label={statusText(requestType)} /></td>
                  <td><StatusBadge status={record.status_group || requestStatus} label={record.display_status || statusText(requestStatus)} /></td>
                  <td>{statusText(returnReason)}</td>
                  <td>{value(record.firearm_condition)}</td>
                  <td>{formatDate(record.returned_at || record.created_at)}</td>
                  <td>{formatDate(record.confirmed_at)}</td>
                  <td><span className="records-table-muted">Read-only</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RecordsTableCard>
      {canReturn && (
        <FormModal
          open={drawerOpen}
          title="Submit Return"
          description="Submit a return request using the current Accountable return workflow."
          onClose={resetReturnForm}
        >
          <form className="inspector-form" onSubmit={submit}>
          <label>Issuance<select value={selected} onChange={(event) => setSelected(event.target.value)}><option value="">Select issued firearm</option>{returnEligibleIssuances.map((issuance) => <option key={issuance.id} value={issuance.id}>{issuance.firearm_serial} — {issuance.officer_name}</option>)}</select></label>
          {returnEligibleIssuances.length === 0 && (
            <p className="form-helper-muted full-width">
              No return-eligible issued firearm found. Incident-closed, returned, revoked, lost, stolen, or beyond-economical-repair firearms cannot use the normal return workflow.
            </p>
          )}
          <label>Return reason<select value={reason} onChange={(event) => setReason(event.target.value)}><option value="">Select reason</option>{reasonOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label>Current firearm condition<select value={condition} onChange={(event) => setCondition(event.target.value)}><option value="">Select condition</option>{conditionOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label className="full-width">Return-condition image<input aria-label="Return-condition image" type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" onChange={(event) => setEvidenceFile(event.target.files?.[0] || null)} /></label>
          {evidenceFile && evidencePreviewUrl ? (
            <div className="firearm-evidence-selection full-width">
              <img src={evidencePreviewUrl} alt={`Selected ${evidenceFile.name} preview`} />
              <div><strong>{evidenceFile.name}</strong><span>{evidenceFile.type}</span></div>
            </div>
          ) : null}
          <label>Remarks<textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} /></label>
          <div className="form-actions full-width">
            <button className="btn btn-primary" type="submit" disabled={submitting || !selected || !reason || !condition || !evidenceFile}><Archive size={14} />{submitting ? 'Submitting return...' : 'Submit return'}</button>
            <button className="btn btn-secondary" type="button" onClick={resetReturnForm}>Cancel</button>
          </div>
          </form>
        </FormModal>
      )}
    </div>
  );
};

export const AccountableCustodyHistory: React.FC = () => {
  // FRS Firearm Management Module: read-only custody history.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const [logs, setLogs] = React.useState<any[]>([]);
  const [error, setError] = React.useState('');
  React.useEffect(() => {
    inventoryService.getCustodyLogs(adminFilter).then(setLogs).catch(() => setError('Custody history is temporarily unavailable.'));
  }, [adminFilter]);
  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Custody History</h1><p>Read-only custody history for accountable records.</p></div></div>
      <FRSNote>FRS Firearm Management Module: assigned officer, issue/return dates, and current status. Historical records are not editable.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <section className="panel">
        {logs.length === 0 ? <EmptyState title="No custody records" copy="No custody records were returned." /> : (
          <div className="table-wrap"><table><thead><tr><th>Firearm</th><th>Assigned officer</th><th>Action</th><th>Date issued / returned</th><th>Current status</th></tr></thead><tbody>
            {logs.map((log) => <tr key={log.id}><td>{value(log.firearm_serial)}</td><td>{value(log.officer_name || log.officer_badge)}</td><td>{statusText(log.action_type_display || log.action_type)}</td><td>{formatDate(log.created_at || log.timestamp)}</td><td>{statusText(log.new_state?.firearm_status || log.new_state?.status || log.metadata?.status || log.action_type)}</td></tr>)}
          </tbody></table></div>
        )}
      </section>
    </div>
  );
};

export const AccountableIncidentView: React.FC = () => {
  // FRS Firearm Management Module: submit incident reports where RBAC permits and view submitted reports.
  const { adminDepartmentId } = useAdminDepartmentContext();
  const adminFilter = React.useMemo(() => withAdminDepartmentParam({}, adminDepartmentId), [adminDepartmentId]);
  const canReport = has('firearm.incident.report');
  const [incidents, setIncidents] = React.useState<any[]>([]);
  const [firearms, setFirearms] = React.useState<Firearm[]>([]);
  const [form, setForm] = React.useState<FirearmIncidentPayload>({
    firearm: 0,
    incident_type: 'lost',
    incident_date: '',
    location: '',
    description: '',
  });
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [incidentTypeFilter, setIncidentTypeFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const { options: incidentTypeOptions } = useReferenceOptions('incident_classifications');
  const { options: incidentStatusOptions } = useReferenceOptions('incident_statuses');

  const load = React.useCallback(async () => {
    const [nextIncidents, nextFirearms] = await Promise.all([
      inventoryService.getFirearmIncidents(adminFilter),
      inventoryService.getFirearms(adminFilter),
    ]);
    setIncidents(nextIncidents);
    setFirearms(nextFirearms);
  }, [adminFilter]);

  React.useEffect(() => {
    void load().catch(() => setError('Incident reports are temporarily unavailable.'));
  }, [load]);

  const visibleIncidents = React.useMemo(() => incidents.filter((incident) => {
    const type = incident.incident_type || '';
    const status = incident.status || '';
    const statusGroup = incident.status_group || status;
    const query = `${incident.firearm_serial || ''} ${type} ${status} ${incident.description || ''}`.toLowerCase();
    return query.includes(search.toLowerCase())
      && (!incidentTypeFilter || type === incidentTypeFilter)
      && (!statusFilter || statusGroup === statusFilter);
  }), [incidentTypeFilter, incidents, search, statusFilter]);

  const resetIncidentForm = () => {
    setForm({ firearm: 0, incident_type: 'lost', incident_date: '', location: '', description: '' });
    setDrawerOpen(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const missingFields = [
      !form.firearm ? 'Firearm' : '',
      !form.incident_type ? 'Incident type' : '',
      !form.incident_date ? 'Incident date' : '',
      !form.location.trim() ? 'Location / operational area' : '',
      !form.description.trim() ? 'Description' : '',
    ].filter(Boolean);
    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}.`);
      return;
    }
    if (form.incident_date > today()) {
      setError('Incident date cannot be later than today.');
      return;
    }
    try {
      await inventoryService.createFirearmIncident({
        ...form,
        location: form.location.trim(),
        description: form.description.trim(),
      });
      setMessage('Incident report submitted.');
      resetIncidentForm();
      setError('');
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Incident report could not be submitted.'));
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Incident Reports</h1><p>Submit incident reports and view submitted reports.</p></div></div>
      <FRSNote>Incident submission uses the five approved Incident Classification options.</FRSNote>
      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Incident records"
        description="Review submitted incident records before filing a new report."
        meta={`${visibleIncidents.length} record${visibleIncidents.length === 1 ? '' : 's'}`}
        actions={canReport ? <button className="btn btn-primary" type="button" onClick={() => { setError(''); setDrawerOpen(true); }}>+ Submit Incident Report</button> : null}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="firearm, type, status, description"
            onSearchChange={setSearch}
            resultCount={`Showing ${visibleIncidents.length} of ${incidents.length}`}
            filters={(
              <>
                <select aria-label="Incident type filter" value={incidentTypeFilter} onChange={(event) => setIncidentTypeFilter(event.target.value)}>
                  <option value="">All incident types</option>
                  {incidentTypeOptions.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
                <select aria-label="Incident status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">All statuses</option>
                  {incidentStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
              </>
            )}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Firearm</th><th>Incident Type</th><th>Date</th><th>Incident Status</th><th>Firearm Status</th><th>Description</th><th>Action</th></tr></thead>
          <tbody>
            {visibleIncidents.length === 0 ? (
              <tr><td colSpan={7}><EmptyTableState title="No incident records" description="No Accountable incident records matched the current search and filters." /></td></tr>
            ) : visibleIncidents.map((incident) => (
              <tr key={incident.id}>
                <td>{value(incident.firearm_serial)}</td>
                <td><TypeBadge type={incident.incident_type || ''} label={incident.incident_classification || statusText(incident.incident_type)} /></td>
                <td>{formatDate(incident.incident_date)}</td>
                <td><StatusBadge status={incident.status_group || incident.status || ''} label={incident.display_status || statusText(incident.status)} /></td>
                <td><StatusBadge status={incident.firearm_status || ''} label={statusText(incident.firearm_status)} /></td>
                <td>{value(incident.description)}</td>
                <td><span className="records-table-muted">Read-only</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>
      {canReport && (
        <FormModal
          open={drawerOpen}
          title="Submit Incident Report"
          description="File an incident report using the existing backend validation rules."
          onClose={resetIncidentForm}
        >
          <form className="inspector-form" onSubmit={submit}>
          <label>Firearm<select value={form.firearm || ''} onChange={(event) => setForm({ ...form, firearm: Number(event.target.value) })}><option value="">Select firearm</option>{firearms.map((firearm) => <option key={firearm.id} value={firearm.id}>{firearm.serial_number}</option>)}</select></label>
          <label>Incident type<select value={form.incident_type} onChange={(event) => setForm({ ...form, incident_type: event.target.value })}>{incidentTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label>Incident date<input type="date" max={today()} value={form.incident_date} onChange={(event) => setForm({ ...form, incident_date: event.target.value })} /></label>
          <label>Location / operational area<input value={form.location} placeholder="Operational area" onChange={(event) => setForm({ ...form, location: event.target.value })} /></label>
          <label className="full-width">Description<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
          <div className="form-actions full-width">
            <button className="btn btn-primary" type="submit"><AlertTriangle size={14} />Submit incident report</button>
            <button className="btn btn-secondary" type="button" onClick={resetIncidentForm}>Cancel</button>
          </div>
          </form>
        </FormModal>
      )}
    </div>
  );
};

const ACCOUNTABLE_NOTIFICATION_LABELS: Record<string, string> = {
  ltopf_expiring: 'LTOPF Expiring Soon',
  ltopf_expiry: 'LTOPF Expiring Soon',
  ltopf_expiring_soon: 'LTOPF Expiring Soon',
  ltopf_expired: 'LTOPF Expired',
  are_expiring: 'ARE Expiring Soon',
  are_expiry: 'ARE Expiring Soon',
  are_expiring_soon: 'ARE Expiring Soon',
  are_expired: 'ARE Expired',
  are_expiration_missing: 'Missing ARE Expiration',
  are_upload_needed: 'ARE Upload Needed',
  are_uploaded: 'ARE Uploaded',
  issuance_approved: 'Issuance Approved',
  issuance_rejected: 'Issuance Rejected',
  issuance_submitted: 'Issuance Submitted',
  return_due: 'Return Due',
};

const accountableNotificationLabel = (type?: string): string =>
  ACCOUNTABLE_NOTIFICATION_LABELS[type || ''] || statusText(type || 'Notification');

const accountableNotificationContext = (notification: any): string => {
  const parts = [
    notification.firearm_serial || notification.firearm_serial_number || notification.serial_number,
    notification.officer_name || notification.officer_badge,
    notification.department_name || notification.department || notification.unit,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : 'No related record listed';
};

const accountableNotificationMessage = (notification: any): string =>
  notification.message || `${accountableNotificationLabel(notification.notification_type || notification.type)} requires review.`;

export const AccountableNotifications: React.FC = () => {
  const { adminDepartmentId } = useAdminDepartmentContext();
  const emptyPage: PaginatedResult<Notification> = {
    count: 0, next: null, previous: null, results: [],
  };
  const [pageData, setPageData] = React.useState<PaginatedResult<Notification>>(emptyPage);
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedNotification, setSelectedNotification] = React.useState<NotificationInboxItem | null>(null);
  const [feedback, setFeedback] = React.useState('');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    const filters = withAdminDepartmentParam({
      ...(activeFilter !== 'all' ? { category: activeFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(searchQuery ? { search: searchQuery } : {}),
      page,
    }, adminDepartmentId);
    setPageData(await notificationService.getSystemNotificationsPage(filters));
  }, [activeFilter, adminDepartmentId, page, searchQuery, statusFilter]);

  React.useEffect(() => {
    void load().catch(() => setError('Notifications are unavailable or not authorized.'));
  }, [load]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const openNotification = (item: NotificationInboxItem) => {
    const previousStatus = item.status;
    const readItem = { ...item, status: 'read' };
    setSelectedNotification(readItem);
    setPageData((current) => ({
      ...current,
      results: current.results.map((notification) =>
        notification.id === item.id ? { ...notification, status: 'read' } : notification
      ),
    }));
    void markNotificationReadIfUnread(item.id, previousStatus)
      .catch(() => {
        setPageData((current) => ({
          ...current,
          results: current.results.map((notification) =>
            notification.id === item.id ? { ...notification, status: previousStatus } : notification
          ),
        }));
        setSelectedNotification((current) => (
          current?.id === item.id ? { ...current, status: previousStatus } : current
        ));
      });
  };

  const dismissSelectedNotification = async () => {
    if (!selectedNotification) return;
    try {
      await notificationService.dismissNotification(Number(selectedNotification.id));
      setSelectedNotification(null);
      setFeedback('Notification dismissed.');
      if (pageData.results.length === 1 && page > 1) {
        setPage((current) => current - 1);
        return;
      }
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Notification could not be dismissed.'));
    }
  };

  const inboxItems: NotificationInboxItem[] = pageData.results.map((notification) => ({
    id: notification.id,
    title: notification.title || accountableNotificationLabel(notification.notification_type),
    message: accountableNotificationMessage(notification),
    notification_type: notification.notification_type,
    severity: notification.severity,
    status: notification.status,
    officer_name: notification.officer_name,
    officer_badge: notification.officer_badge,
    related_context: accountableNotificationContext(notification),
    actionLabel: 'View Details',
    generated_at: notification.generated_at,
    created_at: notification.created_at,
    due_date: notification.due_date,
  }));

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Notifications</h1><p>LTOPF, ARE, and issuance workflow notifications.</p></div></div>
      <WorkflowFeedback message={feedback} onDismiss={() => setFeedback('')} />
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <NotificationInbox
        title="Accountable Inbox"
        items={inboxItems}
        emptyTitle="No notifications"
        emptyDescription="No accountable notifications matched the current filters."
        onSelect={openNotification}
        toolbar={(
          <CompactTableToolbar
            className="notification-toolbar"
            searchValue={searchInput}
            searchPlaceholder="Search title, message, officer, firearm, department"
            searchLabel="Search notifications"
            onSearchChange={setSearchInput}
            filters={(
              <>
                <div className="segmented-tabs" role="tablist" aria-label="Notification filters">
                  {[
                    ['all', 'All'], ['ltopf', 'LTOPF'], ['are', 'ARE'],
                    ['issuance', 'Issuance'], ['returns', 'Returns'], ['conflict', 'Conflict'],
                  ].map(([key, label]) => <button key={key} className={activeFilter === key ? 'active' : ''} onClick={() => { setPage(1); setActiveFilter(key); }} type="button">{label}</button>)}
                </div>
                <select aria-label="Notification status filter" value={statusFilter} onChange={(event) => { setPage(1); setStatusFilter(event.target.value); }}>
                  <option value="">All statuses</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </>
            )}
            resultCount={`${pageData.count} matching notification${pageData.count === 1 ? '' : 's'}`}
            actions={(
              <div className="notification-pagination">
                <button className="btn btn-secondary" type="button" disabled={!pageData.previous} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
                <span>Page {page}</span>
                <button className="btn btn-secondary" type="button" disabled={!pageData.next} onClick={() => setPage((current) => current + 1)}>Next</button>
              </div>
            )}
          />
        )}
      />
      {selectedNotification && (
        <DetailDrawer
          open={Boolean(selectedNotification)}
          title={selectedNotification.title || accountableNotificationLabel(selectedNotification.notification_type)}
          subtitle={selectedNotification.related_context}
          meta={<StatusBadge status={selectedNotification.status || 'notification'} label={statusText(selectedNotification.status)} />}
          onClose={() => setSelectedNotification(null)}
          closeLabel="Close notification details"
          footer={has('notification.system.dismiss') ? <button className="btn btn-secondary" type="button" onClick={dismissSelectedNotification}>Dismiss</button> : null}
        >
          <div className="detail-grid">
            <div><span>Type</span><strong>{accountableNotificationLabel(selectedNotification.notification_type)}</strong></div>
            <div><span>Status</span><strong>{selectedNotification.status === 'dismissed' ? 'Dismissed' : statusText(selectedNotification.status)}</strong></div>
            <div><span>Severity</span><strong>{statusText(selectedNotification.severity)}</strong></div>
            <div><span>Related officer</span><strong>{value(selectedNotification.officer_name || selectedNotification.officer_badge)}</strong></div>
            <div><span>Due date</span><strong>{formatDate(selectedNotification.due_date)}</strong></div>
            <div><span>Generated</span><strong>{formatDate(selectedNotification.generated_at || selectedNotification.created_at)}</strong></div>
            <div className="full-width"><span>Message</span><p>{selectedNotification.message}</p></div>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
};
